import { Api, NestApi } from '../../../redux/api/axiosInstance';

export interface Project {
  project_id: number;
  name: string;
  description: string | null;
  status: 'active' | 'archived';
  created_by: number | null;
  created_at: string;
  updated_at: string;
  job_count: number;
  member_count: number;
}

export interface CreateProjectPayload {
  name: string;
  description?: string;
  status?: 'active' | 'archived';
}

export type UpdateProjectPayload = Partial<CreateProjectPayload>;

export interface ProjectListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'archived';
  sort_by?: 'name' | 'updated_at' | 'created_at' | 'job_count' | 'member_count';
  sort_order?: 'asc' | 'desc';
}

export interface ProjectListResponse {
  data: Project[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface ProjectJobRow {
  job_id: number;
  job_title: string;
  job_status: string;
  job_type: string | null;
  job_location: string | null;
  no_of_openings: number | null;
  open_date: string | null;
  close_date: string | null;
  company_id: number | null;
  company_name: string | null;
  applicant_count: number;
}

export interface OpenJob {
  job_id: number;
  job_title: string;
  job_status: string;
  job_type: string | null;
  job_location: string | null;
  company_name: string | null;
  no_of_openings: number | null;
}

export interface JobCandidate {
  candidate_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  current_title: string | null;
  current_org: string | null;
  application_status: string | null;
  ApplicationStatus: string | null;
  [key: string]: any;
}

export interface ProjectMember {
  member_id: number;
  user_id: number;
  role: 'viewer';
  invited_by: number | null;
  invited_at: string;
  accepted_at: string | null;
  name: string;
  email: string;
  isEmailVerified: boolean;
}

// The nest-api's TransformInterceptor wraps every response in
// { success, statusCode, data, meta?, timestamp }. Unwrap with the standard
// repo idiom (`res.data?.data ?? res.data`) so this also works in tests/mocks
// or against any backend that doesn't wrap.
const unwrap = <T>(res: { data: unknown }): T => {
  const env = res.data as { data?: T } | T;
  return (env && typeof env === 'object' && 'data' in env && (env as { data?: T }).data !== undefined
    ? (env as { data: T }).data
    : (env as T));
};

export const listProjects = async (params: ProjectListParams = {}): Promise<ProjectListResponse> => {
  const res = await Api.get('/projects', { params });
  // Paginated: interceptor hoists `meta` to top level alongside `data`.
  const env = res.data as { data?: Project[]; meta?: ProjectListResponse['meta'] } | ProjectListResponse;
  if (env && 'meta' in env && env.meta && Array.isArray((env as { data?: unknown[] }).data)) {
    return { data: (env as { data: Project[] }).data, meta: (env as { meta: ProjectListResponse['meta'] }).meta };
  }
  // Fallback if no envelope: bare PaginatedResult
  return env as ProjectListResponse;
};

export const getProject = async (id: number): Promise<Project> => {
  const res = await Api.get(`/projects/${id}`);
  return unwrap<Project>(res);
};

export const createProject = async (
  payload: CreateProjectPayload,
): Promise<{ message: string; project_id: number }> => {
  const res = await Api.post('/projects', payload);
  return unwrap<{ message: string; project_id: number }>(res);
};

export const updateProject = async (
  id: number,
  payload: UpdateProjectPayload,
): Promise<{ message: string }> => {
  const res = await Api.patch(`/projects/${id}`, payload);
  return unwrap<{ message: string }>(res);
};

export const archiveProject = async (id: number): Promise<{ message: string }> => {
  const res = await Api.delete(`/projects/${id}`);
  return unwrap<{ message: string }>(res);
};

export const getProjectJobs = async (id: number): Promise<{ data: ProjectJobRow[] }> => {
  const res = await Api.get(`/projects/${id}/jobs`);
  return unwrap<{ data: ProjectJobRow[] }>(res);
};

export interface ProjectRecordingRow {
  id: number;
  candidate_id: number;
  title: string | null;
  url: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
}

export const getProjectRecordings = async (id: number): Promise<ProjectRecordingRow[]> => {
  const res = await Api.get(`/projects/${id}/recordings`);
  const d = unwrap<{ data: ProjectRecordingRow[] }>(res);
  return Array.isArray(d?.data) ? d.data : [];
};

export const getProjectMembers = async (id: number): Promise<{ data: ProjectMember[] }> => {
  const res = await Api.get(`/projects/${id}/members`);
  return unwrap<{ data: ProjectMember[] }>(res);
};

export const removeProjectMember = async (
  projectId: number,
  memberId: number,
): Promise<{ message: string }> => {
  const res = await Api.delete(`/projects/${projectId}/members/${memberId}`);
  return unwrap<{ message: string }>(res);
};

export const updateProjectMember = async (
  projectId: number,
  memberId: number,
  payload: { name?: string; email?: string },
): Promise<{ message: string }> => {
  const res = await Api.patch(`/projects/${projectId}/members/${memberId}`, payload);
  return unwrap<{ message: string }>(res);
};

export const getOpenJobs = async (): Promise<OpenJob[]> => {
  const res = await NestApi.get('/jobs', { params: { job_status: 'open', page: 1, limit: 200 } });
  const body = res.data?.data ?? res.data;
  const arr = Array.isArray(body) ? body : (body?.data ?? []);
  return arr as OpenJob[];
};

export const linkJobToProject = async (jobId: number, projectId: number): Promise<void> => {
  await NestApi.put(`/jobs/${jobId}`, { project_id: projectId });
};

// Client (external user) submits a job to their project — lands as 'pending' approval.
export interface ClientJobPayload {
  job_title: string;
  job_description?: string;
  job_location?: string;
  no_of_openings?: number;
  min_experience?: number;
  max_experience?: number;
  skills?: string;
}
export const submitProjectJob = async (
  projectId: number,
  payload: ClientJobPayload,
): Promise<{ message: string; job_id?: number }> => {
  const res = await NestApi.post('/jobs', { ...payload, project_id: projectId });
  return res.data;
};

// Recruiter approves a (client-submitted) pending job without auto-posting to job boards.
export const approveProjectJob = async (jobId: number): Promise<void> => {
  await NestApi.post(`/jobs/${jobId}/approve`, { platforms: [], publishPublic: false });
};

// ── Shared meetings (recordings + transcripts) ──────────────────────────────
export interface ProjectMeetingRow {
  meeting_link_id: number;
  project_id: number;
  job_id: number | null;
  meeting_id: string;
  online_meeting_id: string | null;
  subject: string | null;
  created_at: string;
}
export interface MeetingArtifacts {
  onlineMeetingId: string | null;
  transcripts: { id: string; createdDateTime?: string }[];
  recordings: { id: string; createdDateTime?: string }[];
  message?: string;
}

const unwrapData = (res: any) => {
  const b = res?.data;
  return b && typeof b === 'object' && 'data' in b ? b.data : b;
};

export const shareMeetingToProject = async (
  projectId: number,
  payload: { meetingId: string; jobId?: number; subject?: string },
): Promise<void> => {
  await Api.post(`/projects/${projectId}/meetings`, payload);
};

export const listProjectMeetings = async (projectId: number): Promise<ProjectMeetingRow[]> => {
  const res = await Api.get(`/projects/${projectId}/meetings`);
  const d = unwrapData(res);
  return Array.isArray(d) ? d : [];
};

export const unshareProjectMeeting = async (projectId: number, linkId: number): Promise<void> => {
  await Api.delete(`/projects/${projectId}/meetings/${linkId}`);
};

export const getProjectMeetingArtifacts = async (projectId: number, linkId: number): Promise<MeetingArtifacts> => {
  const res = await Api.get(`/projects/${projectId}/meetings/${linkId}/artifacts`);
  return unwrapData(res);
};

export const fetchProjectTranscriptText = async (projectId: number, linkId: number, transcriptId: string): Promise<string> => {
  const res = await Api.get(`/projects/${projectId}/meetings/${linkId}/transcripts/${transcriptId}/content`, { responseType: 'text' });
  return res.data as string;
};

export const fetchProjectRecordingObjectUrl = async (projectId: number, linkId: number, recordingId: string): Promise<string> => {
  const res = await Api.get(`/projects/${projectId}/meetings/${linkId}/recordings/${recordingId}/content`, { responseType: 'blob' });
  return URL.createObjectURL(res.data as Blob);
};

// ── Candidate comments (two-way client ⇆ recruiter thread) ──────────────────
export interface CandidateComment {
  comment_id: number;
  project_id: number;
  candidate_id: number;
  job_id: number | null;
  author_user_id: number;
  author_type: 'external' | 'internal';
  author_name: string | null;
  body: string;
  created_at: string;
}

export const listCandidateComments = async (
  projectId: number,
  candidateId?: number,
): Promise<CandidateComment[]> => {
  const res = await Api.get(`/projects/${projectId}/candidate-comments`, {
    params: candidateId != null ? { candidateId } : undefined,
  });
  const d = unwrapData(res);
  return Array.isArray(d) ? d : [];
};

export const addCandidateComment = async (
  projectId: number,
  candidateId: number,
  body: string,
  jobId?: number,
): Promise<CandidateComment> => {
  const res = await Api.post(`/projects/${projectId}/candidates/${candidateId}/comments`, { body, jobId });
  return unwrapData(res);
};

export const deleteCandidateComment = async (
  projectId: number,
  commentId: number,
): Promise<void> => {
  await Api.delete(`/projects/${projectId}/candidate-comments/${commentId}`);
};

export const getJobCandidates = async (jobId: number): Promise<JobCandidate[]> => {
  const res = await NestApi.get(`/jobs/${jobId}/candidates`);
  const data = res.data?.data ?? res.data;
  return Array.isArray(data) ? data : [];
};

export const getShortlistedCandidatesForJob = async (jobId: number): Promise<JobCandidate[]> => {
  const all = await getJobCandidates(jobId);
  return all.filter(c => {
    // Kanban updates Candidates.status — that's returned as `status` or `c.status`
    // ApplicationStatus stays 'applied' and is never updated by the pipeline
    const stage = (
      c.status ?? c.ApplicationStatus ?? c.application_status ?? ''
    ).toLowerCase().trim();
    return stage === 'shortlisted';
  });
};
